/**
 * Bridges `brand.ts` into Mantine's theme and into the raw CSS custom
 * properties that `tokens.css` builds its semantic layer on.
 *
 * This is the only file that knows about both worlds.
 */

import { createTheme, type CSSVariablesResolver, type MantineThemeOverride } from '@mantine/core';
import { generateColors } from '@mantine/colors-generator';
import { brand } from './brand.js';

/** Mantine wants a 10-shade scale; derive it from the single brand hex. */
const teal = generateColors(brand.colors.teal);
const navy = generateColors(brand.colors.navy);
const lime = generateColors(brand.colors.lime);

export const theme: MantineThemeOverride = createTheme({
  colors: { teal, navy, lime },
  primaryColor: 'teal',
  // Shade 6 keeps enough contrast for white text on the teal button fill.
  primaryShade: { light: 6, dark: 5 },

  fontFamily: brand.fonts.body,
  fontFamilyMonospace: brand.fonts.mono,
  headings: {
    fontFamily: brand.fonts.body,
    fontWeight: '650',
    sizes: {
      h1: { fontSize: '1.65rem', lineHeight: '1.2' },
      h2: { fontSize: '1.3rem', lineHeight: '1.25' },
      h3: { fontSize: '1.05rem', lineHeight: '1.3' },
    },
  },

  defaultRadius: 'md',
  radius: brand.radii,
  shadows: {
    xs: brand.shadows.card,
    sm: brand.shadows.cardHover,
    md: brand.shadows.panel,
    lg: brand.shadows.panel,
    xl: brand.shadows.modal,
  },

  focusRing: 'auto',
  cursorType: 'pointer',
  respectReducedMotion: true,

  components: {
    Button: { defaultProps: { radius: 'md' } },
    Modal: { defaultProps: { radius: 'lg' } },
    Paper: { defaultProps: { radius: 'md' } },
    Tooltip: { defaultProps: { withArrow: true, openDelay: 400 } },
  },
});

/**
 * Emits the brand primitives alongside Mantine's own variables. `tokens.css`
 * consumes these; component CSS never touches them directly.
 */
export const cssVariablesResolver: CSSVariablesResolver = () => ({
  variables: {
    '--rm-navy': brand.colors.navy,
    '--rm-teal': brand.colors.teal,
    '--rm-lime': brand.colors.lime,
    '--rm-sand': brand.colors.sand,
    '--rm-ink': brand.colors.ink,

    '--rm-font-body': brand.fonts.body,
    '--rm-font-mono': brand.fonts.mono,

    '--rm-radius-xs': brand.radii.xs,
    '--rm-radius-sm': brand.radii.sm,
    '--rm-radius-md': brand.radii.md,
    '--rm-radius-lg': brand.radii.lg,
    '--rm-radius-xl': brand.radii.xl,

    '--rm-shadow-card-raw': brand.shadows.card,
    '--rm-shadow-card-hover-raw': brand.shadows.cardHover,
    '--rm-shadow-card-dragging': brand.shadows.cardDragging,
    '--rm-shadow-panel': brand.shadows.panel,
    '--rm-shadow-modal': brand.shadows.modal,
  },
  light: {},
  dark: {},
});

export { brand };
